import React, { useEffect, useState } from "react";
import { Tag, Button, message, Empty, Modal, Descriptions, Divider } from "antd";
import { FilePdfOutlined, DownloadOutlined, EyeOutlined, ReloadOutlined, CalendarOutlined, QrcodeOutlined, ShareAltOutlined } from "@ant-design/icons";
import axios from "axios";
import { createPdfDownloadName, downloadFromLink } from "../helpers";
import { QRCodeCanvas } from "qrcode.react";
import "./PdfList.css";

const PdfList = () => {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [selectedQRRecord, setSelectedQRRecord] = useState(null);

  const fetchPDFList = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token") || "";
      const res = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/Pdf/office-to-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data.data || []);
    } catch { message.error("Failed to fetch PDF list"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPDFList(); }, []);

  const formatDate = (d) => {
    try {
      return new Date(d.split(".")[0]).toLocaleDateString("en-IN", {
        day: "numeric", month: "short", year: "numeric",
      });
    } catch { return d; }
  };

  const getFileType = (name = "") => {
    const ext = name.split(".").pop()?.toLowerCase();
    const map = { doc:"Word", docx:"Word", xls:"Excel", xlsx:"Excel", ppt:"PPT", pptx:"PPT", jpg:"Image", jpeg:"Image", png:"Image", webp:"Image" };
    return map[ext] || "File";
  };

  const typeColor = { Word:"#2b5ce6", Excel:"#1a7a43", PPT:"#c84b1e", Image:"#7b3fe4", File:"#888" };

  const handleDownloadQR = (record) => {
    const canvas = document.getElementById(`pdf-qr-canvas-${record.id || record.originalFileName}`);
    if (!canvas) {
      return;
    }

    const pngUrl = canvas.toDataURL("image/png");
    const anchor = document.createElement("a");
    anchor.download = `${createPdfDownloadName(record.originalFileName).replace(/\.pdf$/i, "")}-qr.png`;
    anchor.href = pngUrl;
    anchor.click();
  };

  const handleShare = async (record) => {
    if (!record.s3Url) {
      message.error("File link not available.");
      return;
    }

    try {
      if (navigator.share) {
        await navigator.share({
          title: record.originalFileName || "PDF File",
          text: "Open this PDF file",
          url: record.s3Url,
        });
        return;
      }

      await navigator.clipboard.writeText(record.s3Url);
      message.success("Public file link copied.");
    } catch (error) {
      if (error?.name !== "AbortError") {
        message.error("Unable to share the file link.");
      }
    }
  };

  const handleDownload = async (record, recordKey) => {
    const token = localStorage.getItem("token") || "";
    const fileName = createPdfDownloadName(record.originalFileName);

    setDownloadingId(recordKey);

    try {
      if (record.downloadUrl) {
        try {
          await downloadFromLink(record.downloadUrl, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            fileName,
            fallbackToDirectLink: false,
          });
          return;
        } catch (error) {
          if (error.status === 401 || error.status === 403) {
            message.error("Your session has expired. Please log in again.");
            return;
          }

          if (error.status === 404) {
            message.error("File not found.");
            return;
          }

          if (!record.s3Url) {
            message.error("File not available.");
            return;
          }
        }
      }

      if (record.s3Url) {
        await downloadFromLink(record.s3Url, { fileName });
        return;
      }

      message.error("File not available.");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="pfl-root">
      <div className="pfl-header">
        <div>
          <div className="pfl-title">Converted PDFs</div>
          <div className="pfl-sub">{data.length} file{data.length !== 1 ? "s" : ""} in your history</div>
        </div>
        <Button icon={<ReloadOutlined />} onClick={fetchPDFList} loading={loading} className="pfl-refresh">Refresh</Button>
      </div>

      {loading ? (
        <div className="pfl-skeleton">
          {[1,2,3,4].map((n) => <div key={n} className="pfl-skeleton-row" />)}
        </div>
      ) : data.length === 0 ? (
        <div className="pfl-empty">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No converted PDFs yet" />
          <p className="pfl-empty-hint">Use Word to PDF, Excel to PDF or other tools to get started.</p>
        </div>
      ) : (
        <div className="pfl-list">
          {data.map((record, i) => {
            const recordKey = record.id || i;
            const type = getFileType(record.originalFileName);
            const canView = !!record.s3Url;
            const canDownload =
              record.status === "Converted" &&
              !!(record.downloadUrl || record.s3Url);
            const canGenerateQR = !!record.s3Url;
            const canShare = !!record.s3Url;
            const isDownloading = downloadingId === recordKey;

            return (
              <div key={recordKey} className="pfl-row">
                <div className="pfl-row-icon">
                  <FilePdfOutlined style={{ color: "#e5322d", fontSize: 22 }} />
                </div>
                <div className="pfl-row-info">
                  <div className="pfl-row-name">{record.originalFileName || "Converted PDF"}</div>
                  <div className="pfl-row-meta">
                    <Tag className="pfl-type-tag" style={{ background: typeColor[type] + "15", color: typeColor[type], border: "none" }}>{type}</Tag>
                    {record.actionType && (
                      <Tag className="pfl-type-tag" style={{ background: "#0891b215", color: "#0891b2", border: "none" }}>{record.actionType}</Tag>
                    )}
                    <span className="pfl-row-date"><CalendarOutlined /> {formatDate(record.uploadDate)}</span>
                    <Tag color={record.status === "Converted" ? "green" : "red"} style={{ borderRadius: 100 }}>{record.status}</Tag>
                  </div>
                </div>
                <div className="pfl-row-actions">
                  <Button
                    type="text"
                    icon={<ShareAltOutlined />}
                    className="pfl-action-btn"
                    onClick={() => handleShare(record)}
                    title="Share link"
                    disabled={!canShare || isDownloading}
                  />
                  <Button
                    type="text"
                    icon={<QrcodeOutlined />}
                    className="pfl-action-btn"
                    onClick={() => canGenerateQR && setSelectedQRRecord(record)}
                    title="Generate QR"
                    disabled={!canGenerateQR || isDownloading}
                  />
                  <Button
                    type="text"
                    icon={<EyeOutlined />}
                    className="pfl-action-btn"
                    onClick={() => canView && window.open(record.s3Url, "_blank")}
                    title="View"
                    disabled={!canView || isDownloading}
                  />
                  <Button
                    type="text"
                    icon={!isDownloading ? <DownloadOutlined /> : null}
                    className="pfl-action-btn"
                    onClick={() => handleDownload(record, recordKey)}
                    title="Download"
                    disabled={!canDownload}
                    loading={isDownloading}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        open={Boolean(selectedQRRecord)}
        onCancel={() => setSelectedQRRecord(null)}
        footer={null}
        width={500}
        title={
          <span className="pfl-qr-modal-title">
            {selectedQRRecord?.originalFileName || "PDF QR Code"}
          </span>
        }
        centered
      >
        {selectedQRRecord && (
          <div className="pfl-qr-modal-body">
            <div className="pfl-qr-preview">
              <QRCodeCanvas
                id={`pdf-qr-canvas-${selectedQRRecord.id || selectedQRRecord.originalFileName}`}
                value={selectedQRRecord.s3Url || ""}
                size={220}
                fgColor="#1a1a2e"
                bgColor="#ffffff"
                level="H"
                includeMargin={true}
              />
            </div>
            <Divider />
            <Descriptions
              column={1}
              size="small"
              labelStyle={{ fontWeight: 600, color: "#888", fontSize: 12 }}
              contentStyle={{ fontSize: 13 }}
            >
              <Descriptions.Item label="Type">PDF Link</Descriptions.Item>
              <Descriptions.Item label="Status">{selectedQRRecord.status}</Descriptions.Item>
              <Descriptions.Item label="Created">{formatDate(selectedQRRecord.uploadDate)}</Descriptions.Item>
              <Descriptions.Item label="URL">
                <a
                  href={selectedQRRecord.s3Url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pfl-qr-link"
                >
                  {selectedQRRecord.s3Url}
                </a>
              </Descriptions.Item>
            </Descriptions>
            <Button
              type="primary"
              block
              icon={<DownloadOutlined />}
              className="pfl-qr-download-btn"
              onClick={() => handleDownloadQR(selectedQRRecord)}
            >
              Download QR PNG
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PdfList;
